import { useState } from "react";

import {
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import GroupIcon from "@mui/icons-material/Group";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  type Notification,
  deleteNotification,
  getUnreadCount,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "@/api/notifications";

const POLL_MS = 30_000;

function formatRelative(iso: string, locale: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.round((then - now) / 1000);
  const fmt = new Intl.RelativeTimeFormat(locale === "en" ? "en" : "fr", { numeric: "auto" });
  const abs = Math.abs(diff);
  if (abs < 60) return fmt.format(diff, "second");
  if (abs < 3600) return fmt.format(Math.round(diff / 60), "minute");
  if (abs < 86_400) return fmt.format(Math.round(diff / 3600), "hour");
  return fmt.format(Math.round(diff / 86_400), "day");
}

export function NotificationsBell() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const unreadQuery = useQuery({
    queryKey: ["notifications", "unread-count"],
    queryFn: getUnreadCount,
    refetchInterval: POLL_MS,
    staleTime: 10_000,
  });

  const listQuery = useQuery({
    queryKey: ["notifications", "list"],
    queryFn: () => listNotifications(false, 20),
    enabled: open,
    staleTime: 5_000,
  });

  const markRead = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const markAll = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const remove = useMutation({
    mutationFn: deleteNotification,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const unread = unreadQuery.data ?? 0;

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleClick = (n: Notification) => {
    if (!n.read_at) markRead.mutate(n.id);
    if (n.link) {
      navigate(n.link);
      handleClose();
    }
  };

  return (
    <>
      <Tooltip title={t("notifications.title")} arrow>
        <IconButton onClick={handleOpen} aria-label={t("notifications.title")}>
          <Badge badgeContent={unread} color="error" max={99}>
            {unread > 0 ? <NotificationsIcon /> : <NotificationsNoneIcon />}
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { width: 380, maxWidth: "92vw" } } }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1.25 }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {t("notifications.title")}
          </Typography>
          <Button
            size="small"
            startIcon={<DoneAllIcon fontSize="small" />}
            onClick={() => markAll.mutate()}
            disabled={markAll.isPending || unread === 0}
          >
            {t("notifications.markAllRead")}
          </Button>
        </Stack>
        <Divider />
        <Box sx={{ maxHeight: 480, overflowY: "auto" }}>
          {listQuery.isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (listQuery.data ?? []).length === 0 ? (
            <Box sx={{ px: 2, py: 4, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                {t("notifications.empty")}
              </Typography>
            </Box>
          ) : (
            <List dense disablePadding>
              {(listQuery.data ?? []).map((n) => (
                <ListItemButton
                  key={n.id}
                  onClick={() => handleClick(n)}
                  sx={{
                    py: 1.25,
                    bgcolor: n.read_at ? "transparent" : "action.hover",
                    alignItems: "flex-start",
                    "&:hover .delete-btn": { opacity: 1 },
                  }}
                >
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        {!n.read_at && (
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              bgcolor: "primary.main",
                              flexShrink: 0,
                            }}
                          />
                        )}
                        <Typography variant="body2" sx={{ fontWeight: n.read_at ? 400 : 600 }}>
                          {n.title}
                        </Typography>
                        {n.payload?.backup_of_user_id ? (
                          <Tooltip title={t("notifications.backupTooltip")}>
                            <Chip
                              size="small"
                              icon={<GroupIcon />}
                              color="warning"
                              variant="outlined"
                              label={t("notifications.backupBadge")}
                            />
                          </Tooltip>
                        ) : null}
                      </Stack>
                    }
                    secondary={
                      <>
                        {n.body && (
                          <Typography
                            variant="caption"
                            component="div"
                            color="text.secondary"
                            sx={{ mt: 0.5, whiteSpace: "pre-line" }}
                          >
                            {n.body}
                          </Typography>
                        )}
                        <Typography
                          variant="caption"
                          color="text.disabled"
                          component="div"
                          sx={{ mt: 0.5 }}
                        >
                          {formatRelative(n.created_at, i18n.language)}
                        </Typography>
                      </>
                    }
                  />
                  <IconButton
                    size="small"
                    className="delete-btn"
                    sx={{ opacity: 0, transition: "opacity 120ms" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      remove.mutate(n.id);
                    }}
                    aria-label="delete"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>
      </Popover>
    </>
  );
}
